// ONDE a pílula de sync pode ficar sem cobrir o foco — a parte PURA (sem DOM,
// sem React), pra ser testável em unidade e reusável pela sonda.
//
// O PROBLEMA (ressalva R2 do gate do ux-tv, medida por ele): a pílula é
// `position: fixed` no canto inferior direito e o conteúdo passa POR BAIXO dela.
// No pior caso alcançável — card focado revelado no limite da safe area, canto
// inferior direito — 248x44px (22% do card) ficavam cobertos: a faixa da pílula
// em y=[982,1026] contra o bloco de info do card em y=[1000,1050], z-120 sobre
// z-auto. Some sozinho em 1,6-2,8s, mas enquanto está lá o rótulo do item
// FOCADO some debaixo de um status que o usuário não pediu.
//
// INVARIANTE FIXADO PELO ux-tv: *enquanto visível, o indicador não cobre anel de
// foco nem rótulo de item focável alcançável.*
//
// POR QUE NÃO TEM RESPOSTA ESTÁTICA (o "põe no canto de cima" não fecha):
// nenhuma faixa do canvas é livre de conteúdo o tempo todo. O TopNav some
// sozinho (`data-collapsed`, auto-hide estilo Apple TV+) quando o foco sai dele,
// e aí o conteúdo SOBE e ocupa a faixa do topo; embaixo, o último trilho
// descansa colado na safe area. Reservar espaço no fluxo resolveria, mas é
// exatamente o que o desenho aprovado proíbe ("NÃO empurra layout: fixed no
// canto, fora do fluxo. Nada de reflow que mova o card focado") — e custaria
// 44px de tela permanentes por causa de um indicador que vive 3 segundos.
//
// A REGRA: a pílula CEDE. Ela conhece duas âncoras (a de baixo, padrão, e a de
// cima) com o MESMO inset, e escolhe a que não encosta no retângulo focado. Um
// elemento focado está num lugar só; as duas faixas são separadas por ~900px no
// canvas de 1920x1080, então uma delas sobra — e quando não sobrar (item focado
// mais alto que a distância entre as faixas), a pílula se esconde, porque o
// invariante é sobre estar VISÍVEL. Nada disso mexe no fluxo: muda `top`/
// `bottom` de um elemento que já era `fixed`.
//
// GATILHO É O INVARIANTE, NUNCA A PROXY: quem decide é "o retângulo focado
// encosta na faixa?", não "o usuário apertou pra baixo". Foi essa exata troca
// que plantou o bug do `crossedNavZone` no player (rc21): podar por tipo de
// tecla ignora o BACK, o `setFocus` programático, o zap e o resize. Aqui o
// chamador observa `[data-focused]` e o tempo, não teclas.

/** Só o que importa de um retângulo — evita depender de `DOMRect` (a sonda
 *  monta isto na mão dentro do browser e o teste monta em jsdom). */
export interface Box {
  left: number
  right: number
  top: number
  bottom: number
}

export type SyncPlacement = 'bottom' | 'top' | 'hidden'

export interface PlacementInput {
  /** Retângulos de TUDO que está com `[data-focused]` agora. Plural de
   *  propósito: `[data-focused]` não é garantidamente único (Focusable
   *  aninhado), e o teste do gate lê `querySelectorAll`, não `querySelector`. */
  focusRects: readonly Box[]
  /** Borda horizontal da pílula (não muda com a âncora vertical) + altura. */
  chipLeft: number
  chipRight: number
  chipHeight: number
  /** Distância do topo (âncora de cima) ou do fundo (âncora de baixo) até a
   *  pílula. É o MESMO valor nas duas — `calc(var(--safe-y) + var(--space-6))`. */
  insetY: number
  viewportHeight: number
}

/** Folga somada ao retângulo focado antes de testar interseção.
 *
 *  `getBoundingClientRect` inclui borda e transform (logo o `--focus-scale` já
 *  está dentro), mas NÃO inclui `outline` — e o anel de foco da casa é
 *  border+outline de `--focus-ring-width` (4px). 8px cobre o anel inteiro com
 *  sobra pro arredondamento de subpixel do rasterizador da LG, que não é o do
 *  Chromium onde a sonda mede.
 *
 *  Constante em JS e não token porque não é geometria de layout: é margem de
 *  medição. E ler `--focus-ring-width` em runtime seria pior — o achatador do
 *  `.ipk` REMOVE as definições de custom property, então `getPropertyValue`
 *  devolve string vazia no device empacotado. */
const FOCUS_RING_SLACK_PX = 8

function overlaps(a: Box, b: Box): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top
}

/** A faixa que a pílula ocupa em cada âncora. Exportada porque a sonda e o teste
 *  conferem as duas contra os rects medidos no browser. */
export function chipBoxFor(
  placement: 'bottom' | 'top',
  input: Pick<
    PlacementInput,
    'chipLeft' | 'chipRight' | 'chipHeight' | 'insetY' | 'viewportHeight'
  >,
): Box {
  const { chipLeft, chipRight, chipHeight, insetY, viewportHeight } = input
  const top = placement === 'top' ? insetY : viewportHeight - insetY - chipHeight
  return { left: chipLeft, right: chipRight, top, bottom: top + chipHeight }
}

/**
 * Escolhe a âncora. Ordem deliberada:
 *
 * 1. `bottom` é o PADRÃO e o preferido — é a âncora aprovada pelo ux-tv, a
 *    mesma do LivePip, e onde a pílula fica na esmagadora maioria do boot (o
 *    foco de entrada é `nav-home`, no topo à esquerda: não encosta).
 * 2. `top` só quando `bottom` colide. Uma flutuação a menos: se nada estiver
 *    focado (`focusRects` vazio, janela entre telas), fica em `bottom`.
 * 3. `hidden` só quando as DUAS colidem — item focado mais alto que a distância
 *    entre as faixas. É a saída honesta: o invariante fala de "enquanto
 *    visível", e status nenhum vale cobrir o foco.
 */
export function pickPlacement(input: PlacementInput): SyncPlacement {
  const { focusRects } = input
  if (focusRects.length === 0) return 'bottom'

  const inflated = focusRects.map((r) => ({
    left: r.left - FOCUS_RING_SLACK_PX,
    right: r.right + FOCUS_RING_SLACK_PX,
    top: r.top - FOCUS_RING_SLACK_PX,
    bottom: r.bottom + FOCUS_RING_SLACK_PX,
  }))

  const bottomBox = chipBoxFor('bottom', input)
  if (!inflated.some((f) => overlaps(f, bottomBox))) return 'bottom'

  const topBox = chipBoxFor('top', input)
  if (!inflated.some((f) => overlaps(f, topBox))) return 'top'

  return 'hidden'
}
